import React from 'react';
import Img from 'app/components/Img';
import Logo from 'assets/images/logo.png';
import 'assets/css/shopPackagesSheetPrint.css';
import {
  ShopPackagesPrintModel,
  ShopPackageSheetOrder,
  getPickupDisplayName,
} from './shopPackagesSheetModel';

interface Props {
  model: ShopPackagesPrintModel;
}

const labels = {
  fr: {
    pickupTitle: 'Tableau des commandes enlevées en magasin',
    shop: 'Magasin :',
    deliveryDate: 'Date de livraison magasin :',
    number: 'N°',
    customer: 'Client, société',
    packages: 'Colis',
    pickup: 'Enlèvement',
    signature: 'Signature du client',
  },
  nl: {
    pickupTitle: 'Lijst van afgehaalde bestellingen',
    shop: 'Winkel:',
    deliveryDate: 'Datum van de levering in winkel:',
    number: 'Nr',
    customer: 'Klant, maatschappij',
    packages: 'Pak.',
    pickup: 'Afhaling',
    signature: 'Handtekening',
  },
} as const;

function StatusCircle({ status }: { status: ShopPackageSheetOrder['status'] }) {
  if (status === 'pending') {
    return <span className="shop-packages-sheet__status shop-packages-sheet__status--pending" />;
  }

  if (status === 'done') {
    return <span className="shop-packages-sheet__status shop-packages-sheet__status--done" />;
  }

  return null;
}

function SummaryOrderLabel({ order }: { order: ShopPackageSheetOrder }) {
  const company = order.company ? `, ${order.company.toUpperCase()}` : '';

  return (
    <>
      {order.customer}
      {company}
      {' '}
      <strong>({order.idWithPart})</strong>
    </>
  );
}

function OrderTable({ order }: { order: ShopPackageSheetOrder }) {
  const customerName = order.customer;
  const company = order.company ? order.company.toUpperCase() : null;

  return (
    <table className="shop-packages-sheet__order-table">
      <thead>
        <tr className="shop-packages-sheet__heading-row">
          <td colSpan={2} className="shop-packages-sheet__heading-cell">
            <h2>
              Commande {order.partial ? 'partielle ' : ''}
              {order.idWithPart}
            </h2>
          </td>
          <td colSpan={2} className="shop-packages-sheet__heading-cell">
            <h3>
              Client : {customerName}
              {company && (
                <>
                  {', '}
                  <i>{company}</i>
                </>
              )}
            </h3>
          </td>
        </tr>
      </thead>
      <tbody>
        <tr className="shop-packages-sheet__subhead">
          <td colSpan={2}>Date de commande {order.formattedDate}</td>
          <td colSpan={2}>Nombre total de colis : {order.packageCount}</td>
        </tr>
        <tr className="shop-packages-sheet__colnames">
          <td width="25%"><em>Colis n&deg;</em></td>
          <td width="35%"><em>Référence</em></td>
          <td>&nbsp;</td>
          <td align="right">Contrôle</td>
        </tr>
        <tr className="shop-packages-sheet__divider">
          <td />
          <td />
          <td />
          <td />
        </tr>
        {order.packages.map(pkg => (
          <tr key={pkg.id}>
            <td valign="top">{pkg.formattedId}</td>
            <td valign="top" colSpan={2}>
              <strong>{pkg.name}</strong>
              {pkg.dimensionsText ? ` ${pkg.dimensionsText}` : ''}
            </td>
            <td valign="top" align="right" className="shop-packages-sheet__control-cell">
              <StatusCircle status={order.status} />
            </td>
          </tr>
        ))}
        <tr className="shop-packages-sheet__spacer">
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
        </tr>
      </tbody>
    </table>
  );
}

export default function ShopPackagesSheetPrint({ model }: Props) {
  const copy = labels[model.shopLg];

  return (
    <div className="shop-packages-sheet">
      <h1>Sortie du {model.formattedDate} | {model.title}</h1>
      <p>Note :</p>
      <p>
        {model.orders.length} commandes
        <br />
        Résumé des commandes :
        {' '}
        {model.pendingOrders.map((order, index) => (
          <React.Fragment key={order.partOrderId}>
            {index > 0 ? ' - ' : ''}
            <SummaryOrderLabel order={order} />
          </React.Fragment>
        ))}
      </p>

      {model.orders.map(parts =>
        parts.map(order => (
          <OrderTable key={order.partOrderId} order={order} />
        )),
      )}

      {model.printPickupTable && (
        <div className="shop-packages-sheet__pickup-page page-break">
          <Img src={Logo} alt="Kewlox" className="shop-packages-sheet__logo" />
          <div className="shop-packages-sheet__pickup-title">{copy.pickupTitle}</div>

          <table className="shop-packages-sheet__pickup-table">
            <thead>
              <tr className="shop-packages-sheet__pickup-meta">
                <td colSpan={5}>
                  <div className="shop-packages-sheet__pickup-meta-row">
                    <div>
                      {copy.shop} {model.title}
                    </div>
                    <div>
                      {copy.deliveryDate}
                      {' '}
                      <span>{model.formattedDate}</span>
                    </div>
                  </div>
                </td>
              </tr>
              <tr className="shop-packages-sheet__pickup-head">
                <td>{copy.number}</td>
                <td>{copy.customer}</td>
                <td>{copy.packages}</td>
                <td>{copy.pickup}</td>
                <td>{copy.signature}</td>
              </tr>
            </thead>
            <tbody>
              {model.pendingOrders.map(order => (
                <tr key={`pickup-${order.partOrderId}`}>
                  <td className="shop-packages-sheet__pickup-number">
                    {!order.paid && <span className="shop-packages-sheet__np">NP</span>}
                    {order.idWithPart}
                  </td>
                  <td className="shop-packages-sheet__pickup-customer">
                    {getPickupDisplayName(order)}
                  </td>
                  <td>{order.packageCount}</td>
                  <td className="shop-packages-sheet__pickup-date">. . / . . / . .</td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
